import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  onAuthStateChanged,
  isSignInWithEmailLink,
  signInWithEmailLink,
  updateProfile,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  signOut as firebaseSignOut
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../lib/firebase';

const AuthContext = createContext(null);

// localStorage keys used to carry state across the email "hop"
const EMAIL_STORAGE_KEY = 'lifestory:emailForSignIn';
const CONTINUE_STORAGE_KEY = 'lifestory:postSignInRedirect';
const SHARED_DEVICE_KEY = 'lifestory:sharedDevice';
// sessionStorage flag so the login screen can explain an automatic sign-out.
const SIGNED_OUT_REASON_KEY = 'lifestory:signedOutReason';

// Auto sign-out after this much inactivity. Implements HIPAA's addressable
// "automatic logoff" safeguard (45 CFR 164.312(a)(2)(iii)).
const IDLE_TIMEOUT_MS = 20 * 60 * 1000; // 20 minutes

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch user profile from Firestore
  const fetchProfile = useCallback(async (uid) => {
    try {
      // Load user profile and super admin status in parallel
      const [userDoc, superAdminDoc] = await Promise.all([
        getDoc(doc(db, 'users', uid)),
        getDoc(doc(db, 'superAdmins', uid))
      ]);

      const profile = userDoc.exists() ? userDoc.data() : {};
      profile.isSuperAdmin = superAdminDoc.exists();

      setUserProfile(profile);
      return profile;
    } catch (err) {
      console.error('Error fetching user profile:', err);
    }
    return null;
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        await fetchProfile(firebaseUser.uid);
      } else {
        setUser(null);
        setUserProfile(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [fetchProfile]);

  // Automatic logoff: sign the user out after a period of inactivity. Only
  // armed while signed in; any genuine activity resets the timer.
  useEffect(() => {
    if (!user) return;

    let timer;
    const handleTimeout = async () => {
      try {
        window.sessionStorage.setItem(SIGNED_OUT_REASON_KEY, 'inactivity');
      } catch { /* ignore storage errors */ }
      try {
        await firebaseSignOut(auth);
      } catch (err) {
        console.error('Idle sign-out failed:', err);
      }
    };
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(handleTimeout, IDLE_TIMEOUT_MS);
    };

    const events = ['mousedown', 'keydown', 'touchstart', 'scroll', 'visibilitychange'];
    events.forEach(e => window.addEventListener(e, reset, { passive: true }));
    reset();

    return () => {
      clearTimeout(timer);
      events.forEach(e => window.removeEventListener(e, reset));
    };
  }, [user]);

  /**
   * Send a passwordless sign-in link to the given email.
   *
   * @param {string} email
   * @param {object} [opts]
   * @param {string} [opts.continuePath] - app path to land on after sign-in
   *   completes (e.g. "/staff-invite/abc123"). Stored locally and re-applied
   *   on the completion screen.
   */
  const sendMagicLink = async (email, opts = {}) => {
    const { continuePath, sharedDevice } = opts;

    // Send the sign-in link from our own (quiltt.com) sender via a Cloud
    // Function instead of Firebase's default firebaseapp.com sender, which
    // isn't domain-aligned and lands in spam. The completion flow is unchanged.
    const sendFn = httpsCallable(functions, 'sendSignInLink');
    await sendFn({ email, continueUrl: `${window.location.origin}/auth/complete` });

    // Firebase requires the email again when completing sign-in (anti-phishing).
    // Persist it locally so the completion screen can finish without re-asking.
    window.localStorage.setItem(EMAIL_STORAGE_KEY, email);

    // Carry the device choice across the email hop so completion can pick the
    // right session persistence.
    if (sharedDevice) {
      window.localStorage.setItem(SHARED_DEVICE_KEY, '1');
    } else {
      window.localStorage.removeItem(SHARED_DEVICE_KEY);
    }

    if (continuePath) {
      window.localStorage.setItem(CONTINUE_STORAGE_KEY, continuePath);
    } else {
      window.localStorage.removeItem(CONTINUE_STORAGE_KEY);
    }
  };

  /** True if the current page URL is a Firebase email sign-in link. */
  const isMagicLink = (url = window.location.href) =>
    isSignInWithEmailLink(auth, url);

  /**
   * Complete sign-in from an email link.
   *
   * @param {object} [opts]
   * @param {string} [opts.email] - required if the email isn't in localStorage
   *   (e.g. link opened on a different device than it was requested from).
   * @param {string} [opts.displayName] - set on the account if provided and
   *   the account doesn't already have one (new-account name capture).
   * @returns {{ user: import('firebase/auth').User, continuePath: string|null, isNewUser: boolean }}
   */
  const completeMagicLink = async (opts = {}) => {
    const url = window.location.href;

    if (!isSignInWithEmailLink(auth, url)) {
      throw new Error('This is not a valid sign-in link.');
    }

    const email = opts.email || window.localStorage.getItem(EMAIL_STORAGE_KEY);
    if (!email) {
      // Caller should prompt for the email and retry.
      const e = new Error('EMAIL_REQUIRED');
      e.code = 'auth/email-required';
      throw e;
    }

    // Apply session persistence based on the device choice made when the link
    // was requested. Shared/public device → session-only (cleared when the tab
    // or browser closes); personal device → persistent local session.
    const sharedDevice = window.localStorage.getItem(SHARED_DEVICE_KEY) === '1';
    try {
      await setPersistence(auth, sharedDevice ? browserSessionPersistence : browserLocalPersistence);
    } catch (err) {
      console.error('Failed to set auth persistence:', err);
    }

    const result = await signInWithEmailLink(auth, email, url);

    // Set a display name if we have one and the account lacks it (new accounts).
    if (opts.displayName && !result.user.displayName) {
      try {
        await updateProfile(result.user, { displayName: opts.displayName });
      } catch (err) {
        console.error('Failed to set display name:', err);
      }
    }

    // Clean up the carried state.
    const continuePath = window.localStorage.getItem(CONTINUE_STORAGE_KEY);
    window.localStorage.removeItem(EMAIL_STORAGE_KEY);
    window.localStorage.removeItem(CONTINUE_STORAGE_KEY);
    window.localStorage.removeItem(SHARED_DEVICE_KEY);

    const isNewUser =
      result.user.metadata?.creationTime === result.user.metadata?.lastSignInTime;

    return { user: result.user, continuePath, isNewUser };
  };

  const signOut = async () => {
    return firebaseSignOut(auth);
  };

  /**
   * Re-fetch the user profile from Firestore.
   * Call this after operations that update the user's profile document
   * (e.g., createCommunity, acceptStaffInvite) so the app reflects
   * the new communityId/communityRole without requiring a full page reload.
   */
  const refreshProfile = useCallback(async () => {
    if (user) {
      return await fetchProfile(user.uid);
    }
    return null;
  }, [user, fetchProfile]);

  // Combined user object with auth + profile data
  const currentUser = user ? {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName || userProfile?.name || user.email,
    role: userProfile?.role || 'family', // 'staff' or 'family'
    ...userProfile
  } : null;

  const value = {
    user: currentUser,
    loading,
    sendMagicLink,
    isMagicLink,
    completeMagicLink,
    signOut,
    refreshProfile,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
